/**
 * RestoID — OTP entry: the code boxes, the "code sent to …" chip and the resend row,
 * plus the MFA email/phone code container that drives them.
 * ---------------------------------------------------------------------------
 * The PRESENTATION here was built first, inline, in `custom/pages/VerificationCode.tsx`
 * (the passwordless sign-in OTP screen). It is lifted out verbatim so the MFA
 * email/phone screens render the SAME boxes, the SAME resend row and the SAME chip
 * instead of a second copy that would drift. Two deliberate additions:
 *   - `SentToChip.onEdit` is optional. The MFA screens show a server-masked
 *     identifier the user cannot go back and change, so they render the chip with no
 *     "Изменить" button and no divider;
 *   - the edit button finally gets the hover state the design source
 *     (`custom/design-reference/app.js`) specifies — the page's copy declared the
 *     `transition` for it but never wired the handlers up.
 *
 * `MfaCodeEntry` is a 1:1 restatement of the upstream container
 * `src/containers/MfaCodeVerification/index.tsx`: every hook, state slot, effect,
 * guard and error branch is the upstream one, and the ONLY thing replaced is the
 * markup around them (see the comment on the component for the full list).
 *
 * `custom/pages/VerificationCode.tsx` (the sign-in OTP screen) renders the same
 * three pieces from here, so the design element exists once.
 */
import { type SignInIdentifier } from '@logto/schemas';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useMfaCodeVerification from '@/containers/MfaCodeVerification/use-mfa-code-verification';
import useResendMfaVerificationCode from '@/containers/MfaCodeVerification/use-resend-mfa-verification-code';
import TrustedDeviceOptIn from '@/containers/TrustedDeviceOptIn';
import useTrustedDeviceOptIn from '@/hooks/use-trusted-device-opt-in';
import VerificationCodeInput, { defaultLength } from '@/shared/components/VerificationCode';

import { useCopy } from '../copy';

import { Button, C, FONT, GhostAction, Icon } from './restoid';

/** Upstream's `isCodeReady` — a full row of non-empty digits. */
export const isOtpReady = (code: string[]): boolean =>
  code.length === defaultLength && code.every(Boolean);

/**
 * "Код отправлен на +7 (911) 197-87-87 · Изменить" — the design's chip.
 * Without `onEdit` (MFA, where the identifier is masked and fixed) the button and
 * its divider are not rendered at all.
 */
export const SentToChip = ({ target, onEdit }: { target: string; onEdit?: () => void }) => {
  const c = useCopy();
  const [hov, setHov] = useState(false);
  return (
    <div
      style={{
        margin: '-12px 0 2px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 11,
      }}
    >
      <span style={{ color: C.faint, font: `400 13px/1 ${FONT}`, letterSpacing: '0.01em' }}>
        {c('sentTo')}
      </span>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'stretch',
          background: C.field,
          border: `1px solid ${C.line}`,
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: `inset 0 1px 0 ${C.inset}`,
        }}
      >
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: onEdit ? '10px 8px 10px 16px' : '10px 16px',
            color: C.text,
            font: `600 15px/1 ${FONT}`,
            letterSpacing: '0.03em',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {target}
        </span>
        {onEdit ? (
          <button
            type="button"
            aria-label={c('edit')}
            onClick={onEdit}
            onMouseEnter={() => setHov(true)}
            onMouseLeave={() => setHov(false)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '0 14px',
              border: 'none',
              borderLeft: `1px solid ${C.line}`,
              background: hov ? C.cardHover : 'transparent',
              color: hov ? C.text : C.sub,
              cursor: 'pointer',
              font: `500 12.5px/1 ${FONT}`,
              transition: 'background .15s ease, color .15s ease',
            }}
          >
            <Icon name="edit" size={13} />
            <span>{c('edit')}</span>
          </button>
        ) : null}
      </div>
    </div>
  );
};

/** The countdown, or the resend action once it has run out. */
const ResendRow = ({
  seconds,
  isRunning,
  onResend,
}: {
  seconds: number;
  isRunning: boolean;
  onResend: () => void;
}) => {
  const c = useCopy();
  return (
    <div style={{ display: 'flex', justifyContent: 'center', marginTop: -4 }}>
      {isRunning ? (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            color: C.faint,
            font: `400 13px/1 ${FONT}`,
          }}
        >
          <Icon name="clock" size={14} />
          <span>{`${c('resendIn')} ${seconds} ${c('sec')}`}</span>
        </span>
      ) : (
        <GhostAction icon="refresh" onClick={() => onResend()}>
          {c('resend')}
        </GhostAction>
      )}
    </div>
  );
};

type OtpEntryProps = {
  name: string;
  value: string[];
  onChange: (next: string[]) => void;
  /** Rendered by the upstream input as its inline `ErrorMessage` under the boxes. */
  error?: string;
  seconds: number;
  isRunning: boolean;
  onResend: () => void;
};

/**
 * Boxes + resend row. The boxes stay the PRISTINE upstream `VerificationCodeInput`
 * (paste handling, arrow/backspace navigation, auto-advance, the inline error) —
 * `custom/theme/restoid.css` already repaints them as RestoID filled boxes.
 */
export const OtpEntry = ({
  name,
  value,
  onChange,
  error,
  seconds,
  isRunning,
  onResend,
}: OtpEntryProps) => (
  <>
    <VerificationCodeInput name={name} value={value} error={error} onChange={onChange} />
    <ResendRow seconds={seconds} isRunning={isRunning} onResend={onResend} />
  </>
);

type MfaCodeEntryProps = {
  identifierType: SignInIdentifier.Email | SignInIdentifier.Phone;
  verificationId: string;
};

/**
 * MFA email/phone code entry — upstream `MfaCodeVerification`, markup swapped.
 *
 * Kept from upstream, unchanged:
 *   - `currentVerificationId` state re-synced from the prop by an effect, so a
 *     verification ID that changes under us is picked up;
 *   - `errorCallback` clearing BOTH the code row and the input error, handed to
 *     `useMfaCodeVerification` so a rejected code empties the boxes;
 *   - `inputErrorMessage ?? submitErrorMessage` precedence;
 *   - auto-submit on a complete code ONLY while the trusted-device opt-in is hidden
 *     (otherwise the user would never get to tick it — upstream has a test for this);
 *   - the `isSubmitting` re-entrancy guard around submit;
 *   - "Continue" with an incomplete code sets `error.invalid_passcode` instead of
 *     submitting;
 *   - resend clears the error and the boxes, and adopts the new verification ID;
 *   - the trusted-device opt-in checkbox, rendered by the upstream container so its
 *     `{{count}}` pluralization and dev-feature gate stay upstream's.
 *
 * Changed: the layout only — RestoID resend row instead of the `<Trans>` sentence,
 * the RestoID CTA instead of the upstream `Button`.
 */
export const MfaCodeEntry = ({ identifierType, verificationId }: MfaCodeEntryProps) => {
  const { t } = useTranslation();
  const [codeInput, setCodeInput] = useState<string[]>([]);
  const [inputErrorMessage, setInputErrorMessage] = useState<string>();
  const [currentVerificationId, setCurrentVerificationId] = useState(verificationId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { durationDays, isChecked, setIsChecked } = useTrustedDeviceOptIn();
  const isTrustedDeviceOptInVisible = Boolean(durationDays);

  useEffect(() => {
    setCurrentVerificationId(verificationId);
  }, [verificationId]);

  const errorCallback = useCallback(() => {
    setCodeInput([]);
    setInputErrorMessage(undefined);
  }, []);

  const { errorMessage: submitErrorMessage, onSubmit } = useMfaCodeVerification(
    identifierType,
    currentVerificationId,
    isChecked,
    errorCallback
  );

  const errorMessage = inputErrorMessage ?? submitErrorMessage;

  const { seconds, isRunning, onResendVerificationCode } =
    useResendMfaVerificationCode(identifierType);

  const handleSubmit = useCallback(
    async (code: string[]) => {
      if (isSubmitting) {
        return;
      }

      setInputErrorMessage(undefined);
      setIsSubmitting(true);

      await onSubmit(code.join(''));
      setIsSubmitting(false);
    },
    [onSubmit, isSubmitting]
  );

  const handleResend = useCallback(async () => {
    setInputErrorMessage(undefined);
    setCodeInput([]);
    const newId = await onResendVerificationCode();
    if (newId) {
      setCurrentVerificationId(newId);
    }
  }, [onResendVerificationCode]);

  return (
    <>
      <OtpEntry
        name="mfaCode"
        value={codeInput}
        error={errorMessage}
        seconds={seconds}
        isRunning={isRunning}
        onChange={(code) => {
          setCodeInput(code);
          if (isOtpReady(code) && !isTrustedDeviceOptInVisible) {
            void handleSubmit(code);
          }
        }}
        onResend={() => {
          void handleResend();
        }}
      />
      <TrustedDeviceOptIn
        durationDays={durationDays}
        isChecked={isChecked}
        onChange={setIsChecked}
      />
      <Button
        title={t('action.continue')}
        isLoading={isSubmitting}
        onClick={() => {
          if (!isOtpReady(codeInput)) {
            setInputErrorMessage(t('error.invalid_passcode'));
            return;
          }

          void handleSubmit(codeInput);
        }}
      />
    </>
  );
};
